import type { DefaultTheme } from 'styled-components';
import { createGlobalStyle } from 'styled-components';

const breakpoints = ['600px', '768px', '992px'];

/**
 * Common theme properties.
 */
const theme = {
  fonts: {
    default:
      '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif',
    code: 'ui-monospace,Menlo,Monaco,"Cascadia Mono","Segoe UI Mono","Roboto Mono","Oxygen Mono","Ubuntu Monospace","Source Code Pro","Fira Mono","Droid Sans Mono","Courier New", monospace',
  },
  fontSizes: {
    heading: '5.2rem',
    mobileHeading: '3.6rem',
    title: '2.4rem',
    large: '2rem',
    text: '1.6rem',
    small: '1.4rem',
    tiny: '1.2rem',
  },
  radii: {
    default: '16px',
    button: '8px',
  },
  breakpoints,
  mediaQueries: {
    small: `@media screen and (max-width: ${breakpoints[0] as string})`,
    medium: `@media screen and (min-width: ${breakpoints[1] as string})`,
    large: `@media screen and (min-width: ${breakpoints[2] as string})`,
  },
  shadows: {
    default: '0px 7px 42px rgba(0, 0, 0, 0.25)',
    button: '0px 0px 16.1786px rgba(0, 0, 0, 0.15);',
  },
};

/**
 * Apex Fusion brand accents, shared by both palettes.
 */
const accent = {
  teal: '#00B3B0',
  blue: '#2E9BFF',
  violet: '#7B61FF',
  gradient: 'linear-gradient(100deg, #00B3B0 0%, #2E9BFF 50%, #7B61FF 100%)',
};

/**
 * Light theme color properties.
 */
export const light: DefaultTheme = {
  colors: {
    background: {
      default: '#F7F9FC',
      alternative: '#EDF0F7',
      inverse: '#0B1226',
    },
    icon: {
      default: '#1B2233',
      alternative: '#7A8299',
    },
    text: {
      default: '#1B2233',
      muted: '#6A7390',
      alternative: '#454E68',
      inverse: '#FFFFFF',
    },
    border: {
      default: '#D5DAE8',
    },
    primary: {
      default: '#7B61FF',
      inverse: '#FFFFFF',
    },
    accent,
    card: {
      default: '#FFFFFF',
      elevated: '#F1F3FA',
    },
    success: {
      default: '#1A7F37',
      muted: '#1A7F3726',
    },
    overlay: {
      default: 'rgba(11, 18, 38, 0.45)',
    },
    error: {
      default: '#d73a49',
      alternative: '#b92534',
      muted: '#d73a4919',
    },
  },
  ...theme,
};

/**
 * Dark theme color properties — the Apex Fusion default look.
 */
export const dark: DefaultTheme = {
  colors: {
    background: {
      default: '#070B18',
      alternative: '#0B1226',
      inverse: '#F7F9FC',
    },
    icon: {
      default: '#E8ECF8',
      alternative: '#8B93A8',
    },
    text: {
      default: '#E8ECF8',
      muted: '#8B93A8',
      alternative: '#B9C0D4',
      inverse: '#0B1226',
    },
    border: {
      default: '#232C4A',
    },
    primary: {
      default: '#7B61FF',
      inverse: '#FFFFFF',
    },
    accent,
    card: {
      default: '#0B1226',
      elevated: '#101A33',
    },
    success: {
      default: '#3FB950',
      muted: '#3FB95026',
    },
    overlay: {
      default: 'rgba(4, 8, 20, 0.7)',
    },
    error: {
      default: '#F85149',
      alternative: '#DA3633',
      muted: '#F8514919',
    },
  },
  ...theme,
};

/**
 * Default style applied to the app.
 *
 * @param props - Styled Components props.
 * @returns Global style React component.
 */
export const GlobalStyle = createGlobalStyle`
  html {
    /* 62.5% of the base size of 16px = 10px.*/
    font-size: 62.5%;
  }

  body {
    background-color: ${(props) => props.theme.colors.background?.default};
    color: ${(props) => props.theme.colors.text?.default};
    font-family: ${(props) => props.theme.fonts.default};
    font-size: ${(props) => props.theme.fontSizes.text};
    margin: 0;
  }

  * {
    transition: background-color .1s linear;
    box-sizing: border-box;
  }

  h1, h2, h3, h4, h5, h6 {
    font-size: ${(props) => props.theme.fontSizes.heading};
    ${(props) => props.theme.mediaQueries.small} {
      font-size: ${(props) => props.theme.fontSizes.mobileHeading};
    }
  }

  code {
    background-color: ${(props) => props.theme.colors.background?.alternative};
    font-family: ${(props) => props.theme.fonts.code};
    padding: 1.2rem;
    font-weight: normal;
    font-size: ${(props) => props.theme.fontSizes.text};
  }

  button {
    font-size: ${(props) => props.theme.fontSizes.small};
    border-radius: ${(props) => props.theme.radii.button};
    background-color: ${(props) => props.theme.colors.background?.inverse};
    color: ${(props) => props.theme.colors.text?.inverse};
    border: 1px solid ${(props) => props.theme.colors.background?.inverse};
    font-weight: bold;
    padding: 1rem;
    min-height: 4.2rem;
    cursor: pointer;
    transition: all .2s ease-in-out;

    &:hover {
      background-color: transparent;
      border: 1px solid ${(props) => props.theme.colors.background?.inverse};
      color: ${(props) => props.theme.colors.text?.default};
    }

    &:disabled,
    &[disabled] {
      border: 1px solid ${(props) => props.theme.colors.background?.inverse};
      cursor: not-allowed;
    }

    &:disabled:hover,
    &[disabled]:hover {
      background-color: ${(props) => props.theme.colors.background?.inverse};
      color: ${(props) => props.theme.colors.text?.inverse};
      border: 1px solid ${(props) => props.theme.colors.background?.inverse};
    }
  }

  input, select {
    font-size: ${(props) => props.theme.fontSizes.small};
    font-family: ${(props) => props.theme.fonts.default};
  }
`;
